import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import FiltersPanelThree from "../FiltersPanelThree";
import CommitmentsByDenominationTreemap from "../charts/CommitmentsByDenominationTreemap";
import StackedYearlyCommitmentChart from "../charts/StackedYearlyCommitmentChart";
import type { AnalyticsFilters, YearlyRow } from "../types";

export type ExpandedLoanSections = {
  denomination: boolean;
  instrument: boolean;
  governingLaw: boolean;
  gtc: boolean;
  borrowerRegion: boolean;
};

type PartyCharacteristicsTabProps = {
  expandedLoanSections: ExpandedLoanSections;
  setExpandedLoanSections: Dispatch<SetStateAction<ExpandedLoanSections>>;
  filters: AnalyticsFilters;
  setFilters: Dispatch<SetStateAction<AnalyticsFilters>>;
  creditorRegionOptions: string[];
  borrowerRegionOptions: string[];
  denominationCreditorTypeColumns: string[];
  denominationColumns: string[];
  minYear: number;
  maxYear: number;
  creditorTypeTemporalYearRows: YearlyRow[];
  selectedCreditorTypeColumns: string[];
  creditorTypePalette: string[];
  creditorRegionTemporalYearRows: YearlyRow[];
  filteredYears: number[];
  selectedCreditorRegionColumns: string[];
  creditorRegionPalette: string[];
  borrowerDebtDistressTemporalYearRows: YearlyRow[];
  selectedBorrowerDebtDistressColumns: string[];
  borrowerDebtDistressPalette: string[];
  debtToGdpTemporalYearRows: YearlyRow[];
  selectedDebtToGdpColumns: string[];
  debtToGdpPalette: string[];
  borrowerRegionTemporalYearRows: YearlyRow[];
  selectedBorrowerRegionColumns: string[];
  borrowerRegionPalette: string[];
};

const LOAN_CHARACTERISTICS_Y_AXIS_TITLE = "Commitment Size in Billions of USD";
const stripRegionPrefix = (label: string) =>
  label.replace(/^(CR|BR|CB):\s*/, "");

function LoanSectionChevron() {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 -960 960 960"
      sx={{ width: 24, height: 24, fill: "#ffffff" }}
    >
      <path d="M480-344 240-584l47.33-47.33L480-438.67l192.67-192.66L720-584 480-344Z" />
    </Box>
  );
}

export default function PartyCharacteristicsTab({
  expandedLoanSections,
  setExpandedLoanSections,
  filters,
  setFilters,
  creditorRegionOptions,
  borrowerRegionOptions,
  denominationCreditorTypeColumns,
  denominationColumns,
  minYear,
  maxYear,
  creditorTypeTemporalYearRows,
  selectedCreditorTypeColumns,
  creditorTypePalette,
  creditorRegionTemporalYearRows,
  filteredYears,
  selectedCreditorRegionColumns,
  creditorRegionPalette,
  borrowerDebtDistressTemporalYearRows,
  selectedBorrowerDebtDistressColumns,
  borrowerDebtDistressPalette,
  debtToGdpTemporalYearRows,
  selectedDebtToGdpColumns,
  debtToGdpPalette,
  borrowerRegionTemporalYearRows,
  selectedBorrowerRegionColumns,
  borrowerRegionPalette,
}: PartyCharacteristicsTabProps) {
  return (
    <Stack spacing={2.5}>
      <Card sx={{ px: "1rem", py: "1rem" }}>
        <CardContent>
          <Typography sx={{ fontSize: "1rem", color: "#374151" }}>
            The lending process should be transparent. A key step toward
            achieving that transparency is making it clear who is borrowing and
            lending. Use the interactive visualizations to compare
            characteristics of creditors and borrowers and better understand how
            these change over time.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ px: "1rem", py: "2rem" }}>
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <Accordion
            disableGutters
            expanded={expandedLoanSections.denomination}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                denomination: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Creditor Type
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the proportion of creditor type for our
                  documents by commitment size and over time. A user can see the
                  current makeup of loans by creditor type for a given region,
                  and how the size and creditor type of loans have changed over
                  time.{" "}
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Creditor Type and Year"
                    rows={creditorTypeTemporalYearRows}
                    years={filteredYears}
                    columns={selectedCreditorTypeColumns}
                    palette={creditorTypePalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Creditor Type"
                    rows={creditorTypeTemporalYearRows}
                    years={filteredYears}
                    columns={selectedCreditorTypeColumns}
                    palette={creditorTypePalette}
                    // textColor="#2b2b2b"
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.instrument}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                instrument: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Creditor Region
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the proportion of creditor region for our
                  documents by commitment size and over time. A user can see the
                  current makeup of loans by creditor region for a given region,
                  and how the size and creditor region of loans have changed
                  over time.{" "}
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Creditor Region and Year"
                    rows={creditorRegionTemporalYearRows}
                    years={filteredYears}
                    columns={selectedCreditorRegionColumns}
                    palette={creditorRegionPalette}
                    labelFormatter={stripRegionPrefix}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Creditor Region"
                    rows={creditorRegionTemporalYearRows}
                    years={filteredYears}
                    columns={selectedCreditorRegionColumns}
                    palette={creditorRegionPalette}
                    labelFormatter={stripRegionPrefix}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.governingLaw}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                governingLaw: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Borrower Debt Distress Level
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the proportion of borrower debt distress
                  levels for our documents by commitment size and over time. A
                  user can see the current makeup of loans by borrower debt
                  distress levels for a given region, and how the size and
                  borrower debt distress levels have changed over time.{" "}
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Borrower Debt Distress Level and Year"
                    rows={borrowerDebtDistressTemporalYearRows}
                    years={filteredYears}
                    columns={selectedBorrowerDebtDistressColumns}
                    palette={borrowerDebtDistressPalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Debt Distress Level"
                    rows={borrowerDebtDistressTemporalYearRows}
                    years={filteredYears}
                    columns={selectedBorrowerDebtDistressColumns}
                    palette={borrowerDebtDistressPalette}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            expanded={expandedLoanSections.gtc}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                gtc: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Debt to GDP Ratio
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the proportion of borrower debt to GDP
                  ratio for our documents by commitment size and over time. A
                  user can see the current makeup of loans by borrower debt to
                  GDP ratios for a given region, and how the size and borrower
                  debt to GDP ratios have changed over time.
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Debt to GDP Ratio and Year"
                    rows={debtToGdpTemporalYearRows}
                    years={filteredYears}
                    columns={selectedDebtToGdpColumns}
                    palette={debtToGdpPalette}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Debt to GDP Ratio"
                    rows={debtToGdpTemporalYearRows}
                    years={filteredYears}
                    columns={selectedDebtToGdpColumns}
                    palette={debtToGdpPalette}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            expanded={expandedLoanSections.borrowerRegion}
            onChange={(_, expanded) =>
              setExpandedLoanSections((prev) => ({
                ...prev,
                borrowerRegion: expanded,
              }))
            }
            sx={{
              borderRadius: 1.5,
              boxShadow: "none",
              "&::before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<LoanSectionChevron />}
              sx={{
                bgcolor: "#12448f",
                color: "#fff",
                borderRadius: 1.5,
                minHeight: 44,
                "& .MuiAccordionSummary-content": { my: 0.7 },
                "& .MuiAccordionSummary-expandIconWrapper": {
                  color: "#fff",
                },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Characteristic: Borrower Region
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 3 }}>
              <Stack spacing={2}>
                <Typography sx={{ fontSize: "0.95rem", color: "#374151" }}>
                  These visuals show the proportion of the borrower region for
                  our documents by commitment size and over time. A user can see
                  the current makeup of loans by borrower region for a given
                  creditor type, and how the size and borrower region of loans
                  have changed over time.
                </Typography>
                <FiltersPanelThree
                  filters={filters}
                  setFilters={setFilters}
                  creditorRegionOptions={creditorRegionOptions}
                  borrowerRegionOptions={borrowerRegionOptions}
                  creditorTypeColumns={denominationCreditorTypeColumns}
                  denominationColumns={denominationColumns}
                  minYear={minYear}
                  maxYear={maxYear}
                  horizontal
                  noCard
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <StackedYearlyCommitmentChart
                    title="Commitment Size by Borrower Region and Year"
                    rows={borrowerRegionTemporalYearRows}
                    years={filteredYears}
                    columns={selectedBorrowerRegionColumns}
                    palette={borrowerRegionPalette}
                    labelFormatter={stripRegionPrefix}
                    showStackTotalInTooltip
                    topGrid={60}
                    yAxisTitle={LOAN_CHARACTERISTICS_Y_AXIS_TITLE}
                  />
                  <CommitmentsByDenominationTreemap
                    title="Commitments by Borrower Region"
                    rows={borrowerRegionTemporalYearRows}
                    years={filteredYears}
                    columns={selectedBorrowerRegionColumns}
                    palette={borrowerRegionPalette}
                    labelFormatter={stripRegionPrefix}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* <Typography sx={{ mt: 4, fontSize: "1rem", color: "#374151" }}>
            The above graphics display characteristics of creditors and
            borrowers independent of loan agreement characteristics. This panel
            allows users to compare changes in creditor borrower relationships
            over time or differences in those relationships between subsets of
            loans.
          </Typography> */}
        </CardContent>
      </Card>
    </Stack>
  );
}
